/**
 * Acctoo modal chrome — slate base + per-section accent (matches page themes).
 * Use `section` on StandardModal / PreviewModal / ThemedDialog, or MODAL_SECTIONS.*.
 */

export const MODAL_SECTIONS = {
  DEFAULT: 'default',
  SALES: 'sales',
  PURCHASES: 'purchases',
  PAYMENTS: 'payments',
  EXPENSES_FIXED: 'expensesFixed',
  EXPENSES_VARIABLE: 'expensesVariable',
  LABOUR: 'labour',
  SALARY: 'salary',
  ITEMS: 'items',
  PARTIES: 'parties',
  REPORTS: 'reports',
  SETTINGS: 'settings',
  MANUFACTURING: 'manufacturing',
  LOANS: 'loans',
  DASHBOARD: 'dashboard',
  IMPORT: 'import',
};

const BASE = {
  panel:
    'bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl ring-1 ring-black/5 overflow-hidden flex flex-col shrink-0 min-w-0',
  headerRow: 'flex items-start justify-between gap-3 px-4 py-3.5 sm:px-5 sm:py-4',
  headerTitle: 'text-base sm:text-lg font-bold tracking-tight text-white leading-snug break-words',
  headerHint: 'mt-0.5 text-[11px] text-slate-300 hidden sm:block',
  toolbar:
    'shrink-0 flex flex-wrap items-center gap-2 border-t border-white/10 bg-slate-800/40 px-3 pb-3 pt-2 sm:px-4 sm:pb-4 backdrop-blur-sm',
  closeBtn:
    'shrink-0 rounded-lg p-2 text-white/85 hover:bg-white/15 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50',
  body: 'flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 flex flex-col text-gray-800',
  formFooter:
    'flex shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-3 pt-4 border-t border-slate-100',
  zoomGroup:
    'inline-flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white px-1 py-0.5 shadow-sm',
};

/** Per-section accent overrides (header, stripe, buttons, preview tint). */
const SECTION_ACCENTS = {
  [MODAL_SECTIONS.DEFAULT]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-blue-600 via-sky-500 to-blue-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-sky-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-slate-50/90 to-white',
    btnPrimary:
      'bg-gradient-to-r from-blue-600 to-sky-500 text-white shadow-md hover:brightness-105 focus:ring-blue-500',
    btnPdf: 'border border-sky-200 bg-sky-50 text-sky-900 shadow-sm hover:bg-sky-100 focus:ring-sky-500',
  },
  [MODAL_SECTIONS.SALES]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-blue-600 via-sky-500 to-blue-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-sky-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-sky-50/60 to-white',
    btnPrimary:
      'bg-gradient-to-r from-blue-600 to-sky-500 text-white shadow-md hover:brightness-105 focus:ring-blue-500',
    btnPdf: 'border border-sky-200 bg-sky-50 text-sky-900 shadow-sm hover:bg-sky-100 focus:ring-sky-500',
  },
  [MODAL_SECTIONS.PURCHASES]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-teal-600 via-emerald-500 to-teal-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-teal-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-teal-50/50 to-white',
    btnPrimary:
      'bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-md hover:brightness-105 focus:ring-teal-500',
    btnPdf: 'border border-teal-200 bg-teal-50 text-teal-900 shadow-sm hover:bg-teal-100 focus:ring-teal-500',
  },
  [MODAL_SECTIONS.PAYMENTS]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-amber-600 via-orange-500 to-amber-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-amber-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-amber-50/40 to-white',
    btnPrimary:
      'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-md hover:brightness-105 focus:ring-amber-500',
    btnPdf: 'border border-amber-200 bg-amber-50 text-amber-900 shadow-sm hover:bg-amber-100 focus:ring-amber-500',
  },
  [MODAL_SECTIONS.EXPENSES_FIXED]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-indigo-600 via-indigo-500 to-indigo-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-indigo-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-indigo-50/40 to-white',
    btnPrimary:
      'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-md hover:brightness-105 focus:ring-indigo-500',
    btnPdf: 'border border-indigo-200 bg-indigo-50 text-indigo-900 shadow-sm hover:bg-indigo-100 focus:ring-indigo-500',
  },
  [MODAL_SECTIONS.EXPENSES_VARIABLE]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-violet-600 via-violet-500 to-violet-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-violet-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-violet-50/40 to-white',
    btnPrimary:
      'bg-gradient-to-r from-violet-600 to-violet-700 text-white shadow-md hover:brightness-105 focus:ring-violet-500',
    btnPdf: 'border border-violet-200 bg-violet-50 text-violet-900 shadow-sm hover:bg-violet-100 focus:ring-violet-500',
  },
  [MODAL_SECTIONS.LABOUR]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-teal-600 via-teal-500 to-teal-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-teal-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-teal-50/50 to-white',
    btnPrimary:
      'bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-md hover:brightness-105 focus:ring-teal-500',
    btnPdf: 'border border-teal-200 bg-teal-50 text-teal-900 shadow-sm hover:bg-teal-100 focus:ring-teal-500',
  },
  [MODAL_SECTIONS.SALARY]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-blue-600 via-sky-500 to-blue-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-sky-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-blue-50/50 to-white',
    btnPrimary:
      'bg-gradient-to-r from-blue-600 to-sky-500 text-white shadow-md hover:brightness-105 focus:ring-blue-500',
    btnPdf: 'border border-sky-200 bg-sky-50 text-sky-900 shadow-sm hover:bg-sky-100 focus:ring-sky-500',
  },
  [MODAL_SECTIONS.ITEMS]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-cyan-600 via-sky-500 to-cyan-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-cyan-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-cyan-50/40 to-white',
    btnPrimary:
      'bg-gradient-to-r from-cyan-600 to-sky-600 text-white shadow-md hover:brightness-105 focus:ring-cyan-500',
    btnPdf: 'border border-cyan-200 bg-cyan-50 text-cyan-900 shadow-sm hover:bg-cyan-100 focus:ring-cyan-500',
  },
  [MODAL_SECTIONS.PARTIES]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-fuchsia-600 via-purple-500 to-fuchsia-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-fuchsia-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-fuchsia-50/35 to-white',
    btnPrimary:
      'bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white shadow-md hover:brightness-105 focus:ring-fuchsia-500',
    btnPdf:
      'border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-900 shadow-sm hover:bg-fuchsia-100 focus:ring-fuchsia-500',
  },
  [MODAL_SECTIONS.REPORTS]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-indigo-600 via-slate-500 to-indigo-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-800 via-slate-700 to-indigo-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-indigo-50/30 to-white',
    btnPrimary:
      'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-md hover:brightness-105 focus:ring-indigo-500',
    btnPdf: 'border border-indigo-200 bg-indigo-50 text-indigo-900 shadow-sm hover:bg-indigo-100 focus:ring-indigo-500',
  },
  [MODAL_SECTIONS.SETTINGS]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-slate-600 via-slate-500 to-slate-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-slate-50/90 to-white',
    btnPrimary:
      'bg-gradient-to-r from-slate-600 to-slate-700 text-white shadow-md hover:brightness-105 focus:ring-slate-500',
    btnPdf: 'border border-slate-300 bg-slate-50 text-slate-900 shadow-sm hover:bg-slate-100 focus:ring-slate-500',
  },
  [MODAL_SECTIONS.MANUFACTURING]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-orange-600 via-amber-500 to-orange-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-orange-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-orange-50/40 to-white',
    btnPrimary:
      'bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow-md hover:brightness-105 focus:ring-orange-500',
    btnPdf: 'border border-orange-200 bg-orange-50 text-orange-900 shadow-sm hover:bg-orange-100 focus:ring-orange-500',
  },
  [MODAL_SECTIONS.LOANS]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-amber-700 via-amber-500 to-yellow-500',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-amber-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-amber-50/45 to-white',
    btnPrimary:
      'bg-gradient-to-r from-amber-700 to-amber-600 text-white shadow-md hover:brightness-105 focus:ring-amber-500',
    btnPdf: 'border border-amber-200 bg-amber-50 text-amber-900 shadow-sm hover:bg-amber-100 focus:ring-amber-500',
  },
  [MODAL_SECTIONS.DASHBOARD]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-purple-600 via-violet-500 to-purple-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-purple-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-purple-50/35 to-white',
    btnPrimary:
      'bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-md hover:brightness-105 focus:ring-purple-500',
    btnPdf: 'border border-purple-200 bg-purple-50 text-purple-900 shadow-sm hover:bg-purple-100 focus:ring-purple-500',
  },
  [MODAL_SECTIONS.IMPORT]: {
    topStripe: 'h-1 shrink-0 bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-400',
    header: 'shrink-0 bg-gradient-to-r from-slate-700 via-slate-700 to-emerald-800 text-white',
    previewBody: 'flex-1 overflow-auto bg-gradient-to-b from-emerald-50/40 to-white',
    btnPrimary:
      'bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-md hover:brightness-105 focus:ring-emerald-500',
    btnPdf:
      'border border-emerald-200 bg-emerald-50 text-emerald-900 shadow-sm hover:bg-emerald-100 focus:ring-emerald-500',
  },
};

export function resolveModalSection(section) {
  if (!section) return MODAL_SECTIONS.DEFAULT;
  if (SECTION_ACCENTS[section]) return section;
  const aliases = {
    sales: MODAL_SECTIONS.SALES,
    purchase: MODAL_SECTIONS.PURCHASES,
    purchases: MODAL_SECTIONS.PURCHASES,
    payment: MODAL_SECTIONS.PAYMENTS,
    payments: MODAL_SECTIONS.PAYMENTS,
    fixed: MODAL_SECTIONS.EXPENSES_FIXED,
    variable: MODAL_SECTIONS.EXPENSES_VARIABLE,
    expenses: MODAL_SECTIONS.EXPENSES_FIXED,
    labour: MODAL_SECTIONS.LABOUR,
    advances: MODAL_SECTIONS.LABOUR,
    salary: MODAL_SECTIONS.SALARY,
    salaries: MODAL_SECTIONS.SALARY,
    items: MODAL_SECTIONS.ITEMS,
    parties: MODAL_SECTIONS.PARTIES,
    reports: MODAL_SECTIONS.REPORTS,
    settings: MODAL_SECTIONS.SETTINGS,
    manufacturing: MODAL_SECTIONS.MANUFACTURING,
    loans: MODAL_SECTIONS.LOANS,
    dashboard: MODAL_SECTIONS.DASHBOARD,
    import: MODAL_SECTIONS.IMPORT,
  };
  return aliases[section] || MODAL_SECTIONS.DEFAULT;
}

export function getModalSectionTheme(section = MODAL_SECTIONS.DEFAULT) {
  const key = resolveModalSection(section);
  const accent = SECTION_ACCENTS[key] || SECTION_ACCENTS[MODAL_SECTIONS.DEFAULT];
  return { ...BASE, ...accent, sectionKey: key };
}

/** @deprecated Use getModalSectionTheme(section) — default theme for backwards compat */
export const MODAL_THEME = getModalSectionTheme(MODAL_SECTIONS.DEFAULT);

export const MODAL_BTN = {
  base:
    'inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
  sm: 'px-2.5 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  secondary:
    'border border-slate-300 bg-white text-slate-700 shadow-sm hover:bg-slate-50 focus:ring-slate-400',
  danger: 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-md hover:brightness-105 focus:ring-red-500',
  warning:
    'border border-amber-200 bg-amber-50 text-amber-900 shadow-sm hover:bg-amber-100 focus:ring-amber-500',
  light:
    'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus:ring-slate-400',
  ghost:
    'border border-white/25 bg-white/10 text-white hover:bg-white/20 focus:ring-white/40',
};

export function modalButtonClass(variant = 'primary', size = 'md', extra = '', section = MODAL_SECTIONS.DEFAULT) {
  const theme = getModalSectionTheme(section);
  const v =
    variant === 'primary' || variant === 'info'
      ? theme.btnPrimary
      : variant === 'pdf' || variant === 'success'
        ? theme.btnPdf
        : MODAL_BTN[variant] || theme.btnPrimary;
  const s = size === 'sm' ? MODAL_BTN.sm : MODAL_BTN.md;
  return `${MODAL_BTN.base} ${s} ${v} ${extra}`.trim();
}
